import { Person, Refresh, Save } from '@mui/icons-material';
import
    {
        Alert,
        Avatar,
        Box,
        Button,
        Card,
        CardContent,
        CardHeader,
        CircularProgress,
        Divider,
        FormControl,
        InputLabel,
        LinearProgress,
        MenuItem,
        Select,
        TextField,
        Typography,
        useTheme,
    } from '@mui/material';
import { EGender, type IEmployeeDTO } from '@sharedInterfaces/IEmployee';
import { EAnalysisStatus, type INameGenderData } from '@sharedInterfaces/IEmployeeTalkState';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import type { AppState } from '@store/store';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

export interface INameGenderComponentProperties
{
    showUpdateButton: boolean;
}

export interface ETTNameGenderComponentProps extends ITemplateComponentProps
{
    value?: INameGenderData;
}

export const ETTNameGenderComponent: React.FC<ETTNameGenderComponentProps> = (props) =>
{
    const { component } = props;

    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const ettLangStrings = getLanguageTexts(lang).employeeTalk;
    const oftenUsed = ettLangStrings.oftenUsed;

    const properties = component.properties as INameGenderComponentProperties;
    const showUpdateButton = properties?.showUpdateButton !== undefined ? properties.showUpdateButton : true;

    // Use ETT controller for all complex logic
    const {
        getComponentData,
        updateComponentData,
        hasPersonalDataChanges,
        getPersonalDataChanges,
        hasDescriptionChanges,
        updatePersonalDataWithSaving,
        updateDescriptionWithSaving,
        regeneratePersonalData,
        regenerateDescription,
        isSaving,
        talkState,
        employeeTalk,
        isReadOnlyForCurrentUser,
        isManualMode,
        ignoreChanges,
    } = useEmployeeTalk();

    const currentEmployee = useSelector((state: AppState) => state.employee) as IEmployeeDTO;
    const isOwnTalk = employeeTalk?.employeeId === currentEmployee.id;

    // Get component data (backend pre-populates with employee data)
    const componentData = getComponentData<INameGenderData>(component.id) || {
        firstName: '',
        lastName: '',
        gender: EGender.MALE,
        phoneNumber: '',
        description: '',
    };

    // Local state for immediate UI updates (before blur)
    const [localData, setLocalData] = useState<INameGenderData>(componentData);
    const [descriptionFocused, setDescriptionFocused] = useState<boolean>(false);
    const [isRegeneratingPersonal, setIsRegeneratingPersonal] = useState<boolean>(false);
    const [isRegenerating, setIsRegenerating] = useState<boolean>(false);

    // Refs for AutoFill detection
    const firstNameRef = useRef<HTMLInputElement>(null);
    const lastNameRef = useRef<HTMLInputElement>(null);
    const phoneRef = useRef<HTMLInputElement>(null);
    const genderRef = useRef<HTMLSelectElement>(null);
    const formRef = useRef<HTMLDivElement>(null);

    // Analysis status
    const cvAnalysisStatus = talkState?.analysis.description.status || EAnalysisStatus.IDLE;
    const cvAnalysisData = talkState?.analysis.description.data as string;
    const personalAnalysisStatus = talkState?.analysis.personal?.status || EAnalysisStatus.IDLE;
    const personalAnalysisData = talkState?.analysis.personal?.data as { firstName?: string; lastName?: string; phoneNumber?: string; gender?: string };

    // AutoFill detection helper
    const extractDOMValues = useCallback((): INameGenderData =>
    {
        return {
            firstName: firstNameRef.current?.value || '',
            lastName: lastNameRef.current?.value || '',
            gender: (genderRef.current?.value as EGender) || EGender.MALE,
            phoneNumber: phoneRef.current?.value || '',
            description: localData.description, // Keep existing description
        };
    }, [localData.description]);

    // MutationObserver disabled - only rely on blur events
    // useEffect(() => {
    //     if (!formRef.current) return;

    //     const observer = new MutationObserver(() => {
    //         // Only sync if values changed due to AutoFill (not user typing)
    //         setTimeout(() => {
    //             const domValues = extractDOMValues();
    //             const hasAutoFillChanges = 
    //                 (domValues.firstName !== localData.firstName && domValues.firstName !== '') ||
    //                 (domValues.lastName !== localData.lastName && domValues.lastName !== '') ||
    //                 (domValues.phoneNumber !== localData.phoneNumber && domValues.phoneNumber !== '');

    //             if (hasAutoFillChanges) {
    //                 setLocalData(prev => ({ ...prev, ...domValues }));
    //                 const newData = { ...componentData, ...domValues, description: componentData.description };
    //                 updateComponentData(component.id, newData);
    //             }
    //         }, 200);
    //     });

    //     observer.observe(formRef.current, {
    //         attributes: true,
    //         attributeFilter: ['value'],
    //         subtree: true,
    //         childList: true,
    //     });

    //     return () => {
    //         observer.disconnect();
    //     };
    // }, [localData, componentData, updateComponentData, component.id, extractDOMValues]);

    // Update local state immediately for UI responsiveness (no backend updates)
    const handlePersonalDataChange = useCallback((field: keyof INameGenderData, value: string | EGender) =>
    {
        setLocalData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const handleDescriptionChange = useCallback((newDescription: string) =>
    {
        setLocalData((prev) => ({ ...prev, description: newDescription }));
    }, []);

    // Unified blur handler - use localData instead of DOM
    const handlePersonalBlur = useCallback(() =>
    {
        setTimeout(() =>
        {
            const hasChanges =
                localData.firstName !== componentData.firstName ||
                localData.lastName !== componentData.lastName ||
                localData.gender !== componentData.gender ||
                localData.phoneNumber !== componentData.phoneNumber;

            if (hasChanges)
            {
                updateComponentData(component.id, localData);
            }
        }, 100);
    }, [updateComponentData, component.id, localData, componentData]);

    // Blur handler for description field
    const handleDescriptionBlur = useCallback(() =>
    {
        setDescriptionFocused(false);
        if (localData.description !== componentData.description)
        {
            const newData = { ...componentData, description: localData.description };
            updateComponentData(component.id, newData);
        }
    }, [localData.description, componentData, updateComponentData, component.id]);

    const handleDescriptionFocus = useCallback(() =>
    {
        setDescriptionFocused(true);
    }, []);

    // Simple save handlers - pass current local values
    const handleSavePersonalData = useCallback(async () =>
    {
        const changes = getPersonalDataChanges(component.id, currentEmployee, localData);
        if (changes)
        {
            await updatePersonalDataWithSaving(changes, component.id, currentEmployee);
        }
    }, [localData, getPersonalDataChanges, component.id, currentEmployee, updatePersonalDataWithSaving]);

    const handleSaveDescription = useCallback(async () =>
    {
        if (hasDescriptionChanges(component.id, currentEmployee, localData))
        {
            await updateDescriptionWithSaving(localData.description || '', component.id, currentEmployee);
        }
    }, [localData, hasDescriptionChanges, component.id, currentEmployee, updateDescriptionWithSaving]);

    const handleRegeneratePersonalData = useCallback(async () =>
    {
        setIsRegeneratingPersonal(true);
        try
        {
            await regeneratePersonalData();
        } catch (error)
        {
            console.error('Failed to regenerate personal data:', error);
        } finally
        {
            setTimeout(() => setIsRegeneratingPersonal(false), 5000);
        }
    }, [regeneratePersonalData]);

    const handleRegenerateDescription = useCallback(async () =>
    {
        setIsRegenerating(true);
        try
        {
            await regenerateDescription();
        } catch (error)
        {
            console.error('Failed to regenerate description:', error);
        } finally
        {
            setTimeout(() => setIsRegenerating(false), 5000);
        }
    }, [regenerateDescription]);

    const handleUpdateButtonToggle = useCallback(() =>
    {
        if (props.onEdit)
        {
            props.onEdit({
                ...component,
                properties: {
                    ...properties,
                    showUpdateButton: !showUpdateButton,
                } as INameGenderComponentProperties,
                system: component.system,
            });
        }
    }, [props.onEdit, component, properties, showUpdateButton]);

    // Simple boolean flags for UI - use local data for immediate feedback
    const showPersonalDataSaveButton = hasPersonalDataChanges(component.id, currentEmployee, localData);
    const showDescriptionSaveButton = hasDescriptionChanges(component.id, currentEmployee, localData);

    // Validation: firstName, lastName are required, and gender must be selected
    const isPersonalDataValid = (localData.firstName ?? '').trim() !== '' && (localData.lastName ?? '').trim() !== '' && localData.gender !== '';


    React.useEffect(() =>
    {
        setLocalData(componentData);
    }, [componentData]);

    // Auto-fill from CV analysis disabled - only manual regeneration
    // React.useEffect(() => {
    //     if (personalAnalysisStatus === EAnalysisStatus.COMPLETED && personalAnalysisData) {
    //         const hasEmptyFields = !localData.firstName || !localData.lastName || !localData.phoneNumber;
    //         if (hasEmptyFields) {
    //             const autoFilledData = {
    //                 ...localData,
    //                 firstName: personalAnalysisData.firstName || localData.firstName,
    //                 lastName: personalAnalysisData.lastName || localData.lastName,
    //                 phoneNumber: personalAnalysisData.phoneNumber || localData.phoneNumber,
    //                 gender: (personalAnalysisData.gender as EGender) || localData.gender,
    //             };
    //             setLocalData(autoFilledData);
    //             // Auto-save after short delay
    //             setTimeout(() => {
    //                 const newData = { ...componentData, ...autoFilledData, description: componentData.description };
    //                 updateComponentData(component.id, newData);
    //             }, 500);
    //         }
    //     }
    // }, [personalAnalysisStatus, personalAnalysisData, localData, componentData, updateComponentData, component.id]);

    const renderEditContent = () =>
    {
        return (
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {oftenUsed.settings}
                </Typography>
                <Button
                    variant={showUpdateButton ? 'contained' : 'outlined'}
                    onClick={handleUpdateButtonToggle}
                    color="primary"
                    size="small"
                >
                    {showUpdateButton ? ettLangStrings.hideUpdateButton : ettLangStrings.showUpdateButton}
                </Button>
            </Box>
        );
    };

    const renderViewContent = () =>
    {
        const isReadOnly = isReadOnlyForCurrentUser;

        return (
            <Card sx={{ mb: 3 }} ref={formRef}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    backgroundColor: theme.palette.primary.main,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                <Person />
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                Persönliche Angaben
                            </Typography>
                        </Box>
                    }
                    sx={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        py: 2,
                    }}
                />
                <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* CV Analysis Status for Personal Data */}
                        {personalAnalysisStatus === EAnalysisStatus.LOADING && (
                            <Box sx={{ mb: 2 }}>
                                <Alert severity="info" sx={{ py: 0.5 }}>
                                    Persönliche Daten werden aus CV extrahiert...
                                </Alert>
                                <LinearProgress sx={{ mt: 1 }} />
                            </Box>
                        )}

                        {/* Personal Data Regenerate Button */}
                        {!isReadOnly && !isManualMode && (personalAnalysisStatus === EAnalysisStatus.COMPLETED || personalAnalysisStatus === EAnalysisStatus.ERROR || personalAnalysisData) && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={
                                        isRegeneratingPersonal || personalAnalysisStatus === EAnalysisStatus.LOADING ? (
                                            <CircularProgress size={16} color="inherit" />
                                        ) : (
                                            <Refresh />
                                        )
                                    }
                                    onClick={handleRegeneratePersonalData}
                                    disabled={
                                        isSaving || personalAnalysisStatus === EAnalysisStatus.LOADING || isRegeneratingPersonal
                                    }
                                >
                                    {isRegeneratingPersonal || personalAnalysisStatus === EAnalysisStatus.LOADING
                                        ? 'Extrahiere...'
                                        : 'Persönliche Daten neu extrahieren'}
                                </Button>
                            </Box>
                        )}

                        {/* Personal Data Fields - Hidden during extraction */}
                        {personalAnalysisStatus === EAnalysisStatus.LOADING ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: 0.5 }}>
                                {/* Placeholder fields during extraction */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Vorname"
                                        value=""
                                        variant="outlined"
                                        disabled
                                        placeholder="Wird extrahiert..."
                                    />
                                    <TextField
                                        fullWidth
                                        label="Nachname"
                                        value=""
                                        variant="outlined"
                                        disabled
                                        placeholder="Wird extrahiert..."
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Firmen E-Mail"
                                        value={currentEmployee.email}
                                        variant="outlined"
                                        type="email"
                                        disabled
                                    />
                                    <TextField
                                        fullWidth
                                        label="Telefonnummer"
                                        value=""
                                        variant="outlined"
                                        disabled
                                        placeholder="Wird extrahiert..."
                                    />
                                </Box>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Geschlecht</InputLabel>
                                    <Select
                                        value={EGender.MALE}
                                        label="Geschlecht"
                                        disabled
                                    >
                                        <MenuItem value={EGender.MALE}>Wird extrahiert...</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        ) : (
                            <>
                                {/* Name Fields - Two Column */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Vorname"
                                        value={localData.firstName}
                                        onChange={(e) => handlePersonalDataChange('firstName', e.target.value)}
                                        onBlur={handlePersonalBlur}
                                        variant="outlined"
                                        required
                                        disabled={isReadOnly}
                                        aria-label="Vorname"
                                        inputRef={firstNameRef}
                                        autoComplete="given-name"
                                    />
                                    <TextField
                                        fullWidth
                                        label="Nachname"
                                        value={localData.lastName}
                                        onChange={(e) => handlePersonalDataChange('lastName', e.target.value)}
                                        onBlur={handlePersonalBlur}
                                        variant="outlined"
                                        required
                                        disabled={isReadOnly}
                                        aria-label="Nachname"
                                        inputRef={lastNameRef}
                                        autoComplete="family-name"
                                    />
                                </Box>

                                {/* Email and Phone - Two Column */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Firmen E-Mail"
                                        value={currentEmployee.email}
                                        variant="outlined"
                                        type="email"
                                        disabled
                                        aria-label="Firmen E-Mail"
                                        autoComplete="email"
                                    />
                                    <TextField
                                        fullWidth
                                        label="Telefonnummer"
                                        value={localData.phoneNumber}
                                        onChange={(e) => handlePersonalDataChange('phoneNumber', e.target.value)}
                                        onBlur={handlePersonalBlur}
                                        variant="outlined"
                                        type="tel"
                                        placeholder="+49 123 456789"
                                        disabled={isReadOnly}
                                        aria-label="Telefonnummer"
                                        inputRef={phoneRef}
                                        autoComplete="tel"
                                    />
                                </Box>

                                {/* Gender - Full Width */}
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>Geschlecht</InputLabel>
                                    <Select
                                        value={localData.gender}
                                        label="Geschlecht"
                                        onChange={(e) => handlePersonalDataChange('gender', e.target.value as EGender)}
                                        onBlur={handlePersonalBlur}
                                        disabled={isReadOnly}
                                        aria-label="Geschlecht"
                                        ref={genderRef}
                                    >
                                        <MenuItem value={EGender.MALE}>Männlich</MenuItem>
                                        <MenuItem value={EGender.FEMALE}>Weiblich</MenuItem>
                                        <MenuItem value={EGender.OTHER}>Divers</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        {/* Manager Info Alert */}
                        {!isOwnTalk && showPersonalDataSaveButton && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                Sie bearbeiten die Daten eines anderen Mitarbeiters. Nur der Mitarbeiter selbst kann die
                                Änderungen speichern.
                            </Alert>
                        )}

                        {/* Personal Data Save Button */}
                        {showUpdateButton && showPersonalDataSaveButton && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    mt: 1,
                                    mb: 2,
                                }}
                            >
                                <Button
                                    variant={componentData.ignoredChanges?.personal ? 'contained' : 'outlined'}
                                    color="secondary"
                                    size="small"
                                    onClick={() => ignoreChanges(component.id, 'personal')}
                                    disabled={!isOwnTalk}
                                >
                                    Ignorieren
                                </Button>
                                <Button
                                    variant={componentData.ignoredChanges?.personal ? 'outlined' : 'contained'}
                                    color="primary"
                                    size="small"
                                    startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                    onClick={handleSavePersonalData}
                                    disabled={isSaving || !isOwnTalk || !isPersonalDataValid}
                                >
                                    {isSaving ? 'Speichere...' : 'Persönliche Daten speichern'}
                                </Button>
                            </Box>
                        )}

                        {/* Divider between personal data and description */}
                        <Divider sx={{ my: 2 }} />

                        {/* Description Field */}
                        <Box sx={{ position: 'relative' }}>
                            {cvAnalysisStatus === EAnalysisStatus.LOADING && (
                                <Box sx={{ mb: 1 }}>
                                    <Alert severity="info" sx={{ py: 0.5 }}>
                                        Beschreibung wird generiert...
                                    </Alert>
                                    <LinearProgress sx={{ mt: 1 }} />
                                </Box>
                            )}

                            <FormatedTextInput
                                value={localData.description || ''}
                                placeholder={
                                    cvAnalysisStatus === EAnalysisStatus.LOADING
                                        ? 'Beschreibung wird generiert...'
                                        : 'Ihre berufliche Beschreibung wird hier automatisch aus Ihrem CV generiert...'
                                }
                                onChange={
                                    isReadOnly || cvAnalysisStatus === EAnalysisStatus.LOADING
                                        ? undefined
                                        : handleDescriptionChange
                                }
                                onFocus={
                                    isReadOnly || cvAnalysisStatus === EAnalysisStatus.LOADING
                                        ? undefined
                                        : handleDescriptionFocus
                                }
                                onBlur={
                                    isReadOnly || cvAnalysisStatus === EAnalysisStatus.LOADING
                                        ? undefined
                                        : handleDescriptionBlur
                                }
                                disabled={isReadOnly || cvAnalysisStatus === EAnalysisStatus.LOADING}
                                isAI={
                                    cvAnalysisStatus === EAnalysisStatus.COMPLETED && cvAnalysisData ? true : undefined
                                }
                                fullWidth
                            />

                            {cvAnalysisStatus === EAnalysisStatus.ERROR && (
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {talkState?.analysis.description.error ||
                                        'Fehler bei der CV-Analyse. Bitte versuchen Sie es erneut.'}
                                </Alert>
                            )}
                        </Box>

                        {/* Manager Info Alert for Description */}
                        {!isOwnTalk && showDescriptionSaveButton && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                Sie bearbeiten die Beschreibung eines anderen Mitarbeiters. Nur der Mitarbeiter selbst
                                kann die Änderungen speichern.
                            </Alert>
                        )}

                        {/* Description Save Button */}
                        {showUpdateButton && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    mt: 2,
                                }}
                            >
                                {!isReadOnly && !isManualMode && (cvAnalysisStatus === EAnalysisStatus.COMPLETED || cvAnalysisStatus === EAnalysisStatus.ERROR || cvAnalysisData) && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={
                                            isRegenerating || cvAnalysisStatus === EAnalysisStatus.LOADING ? (
                                                <CircularProgress size={16} color="inherit" />
                                            ) : (
                                                <Refresh />
                                            )
                                        }
                                        onClick={handleRegenerateDescription}
                                        disabled={
                                            isSaving || cvAnalysisStatus === EAnalysisStatus.LOADING || isRegenerating
                                        }
                                    >
                                        {isRegenerating || cvAnalysisStatus === EAnalysisStatus.LOADING
                                            ? 'Generiere...'
                                            : 'Neu generieren'}
                                    </Button>
                                )}
                                {showDescriptionSaveButton && componentData.description && (
                                    <>
                                        <Button
                                            variant={componentData.ignoredChanges?.description ? 'contained' : 'outlined'}
                                            color="secondary"
                                            size="small"
                                            onClick={() => ignoreChanges(component.id, 'description')}
                                            disabled={!isOwnTalk}
                                        >
                                            Ignorieren
                                        </Button>
                                        <Button
                                            variant={componentData.ignoredChanges?.description ? 'outlined' : 'contained'}
                                            color="primary"
                                            size="small"
                                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                            onClick={handleSaveDescription}
                                            disabled={isSaving || !isOwnTalk || descriptionFocused}
                                        >
                                            {isSaving ? 'Speichere...' : 'Beschreibung speichern'}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
        />
    );
};

export default ETTNameGenderComponent;
